// Shared "Check-in Info" leading step for per-form check-in flows.
//
// A check-in schema form reached by its own QR / Submit link has no property-QR
// landing to collect Name/Phone/Email/(Business), so we prepend a "Check-in Info"
// step to the schema — it renders as the FIRST step of the same stepper, before
// the form's own steps. Its fields are keyed under `_checkin.*` so the submit
// handler can lift them into the submission's `checkin_info` block and drop them
// from the schema answers. Business Name is required only for a contractor sign-in
// form. Used by both the public (VisitorForm) and authenticated (FormSubmission)
// per-form paths.

// A form is a contractor check-in ONLY when its purpose is "Contractor Check-in /
// Check-out" — this is what decides the Business Name field, the contractor
// visitor_type, and the bridge to Contractor Management (mirrors the backend
// Form::isContractorCheckInPurpose). Scope is not used for this decision.
export const CONTRACTOR_CHECKIN_PURPOSE = "contractor_check_in";
export const isContractorCheckinForm = (form) =>
  !!(form && form.purpose === CONTRACTOR_CHECKIN_PURPOSE);

export const buildCheckinInfoStep = (isContractor) => ({
  id: "_checkin_info",
  title: "Check-in Info",
  formMeta: {
    columns: 1,
    fields: [
      { key: "_checkin.name", label: "Name", required: true, rules: [{ whitespace: true, message: "Please enter your name" }] },
      { key: "_checkin.phone", label: "Phone Number", required: true, rules: [{ whitespace: true, message: "Please enter your phone number" }] },
      { key: "_checkin.email", label: "Email", required: true, rules: [{ type: "email", message: "Please enter a valid email" }] },
      ...(isContractor
        ? [{ key: "_checkin.business_name", label: "Business / Company Name", required: true, rules: [{ whitespace: true, message: "Please enter your business name" }] }]
        : []),
      { key: "_checkin.purpose", label: "Purpose of Visit" },
    ],
  },
});

// Return a copy of `form` with the Check-in Info step prepended, when it is a
// check-in SCHEMA form; otherwise return it unchanged.
export const withCheckinInfoStep = (form) => {
  if (form && form.is_checkin_form && form.schema_json && Array.isArray(form.schema_json.steps)) {
    const isContractor = isContractorCheckinForm(form);
    return {
      ...form,
      schema_json: {
        ...form.schema_json,
        steps: [buildCheckinInfoStep(isContractor), ...form.schema_json.steps],
      },
    };
  }
  return form;
};

// Lift the leading step's `_checkin` answers into a checkin_info block and remove
// them from the schema answers. Returns { globalForm, checkinInfo }. checkinInfo is
// null when there is no `_checkin` (e.g. a non-check-in form, or the property-QR
// landing which supplies its own checkin_info).
export const liftCheckinInfo = (answers, form) => {
  if (answers && answers._checkin && typeof answers._checkin === "object") {
    const ci = answers._checkin;
    const { _checkin, ...globalForm } = answers;
    return {
      globalForm,
      checkinInfo: {
        name: (ci.name || "").trim(),
        phone: (ci.phone || "").trim(),
        email: (ci.email || "").trim(),
        purpose: (ci.purpose || "").trim(),
        business_name: (ci.business_name || "").trim(),
        // Type comes from the form's PURPOSE (Contractor Check-in / Check-out) —
        // the backend is authoritative, this is advisory. No checkout token here.
        visitor_type: isContractorCheckinForm(form) ? "contractor" : "visitor",
      },
    };
  }
  return { globalForm: answers, checkinInfo: null };
};
